import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";

const TECH_STATUS_OPTIONS = [
  {
    value: "calibration_finished",
    label: "Calibración finalizada (LAB-Técn. asignado)",
  },
  {
    value: "certificates_finished_controlled",
    label: "Certificados finalizados y controlados (LAB-Diego)",
  },
  { value: "uploaded_for_remito", label: "Subido para remitar (LAB-Diego)" },
];

const STATUS_LABELS = {
  delivered_to_lab: "Entregado a laboratorio (ADM-Paola)",
  calibration_finished: "Calibración finalizada (LAB-Técn. asignado)",
  certificates_finished_controlled:
    "Certificados finalizados y controlados (LAB-Diego)",
  uploaded_for_remito: "Subido para remitar (LAB-Diego)",
  remito_generated: "Remito generado (ADM-Paola)",
  client_notified: "Cliente avisado (ADM-Paola/Dali)",
};

const LEGACY_STATUS_TO_NEW = {
  pending: "delivered_to_lab",
  in_progress: "calibration_finished",
  completed: "client_notified",
  blocked: "uploaded_for_remito",
};

const ADMIN_LOCKED_STATUSES = new Set(["remito_generated", "client_notified"]);

function normalizeServiceOrderStatus(status) {
  const s = String(status || "").trim();
  if (!s) return "delivered_to_lab";
  if (STATUS_LABELS[s]) return s;
  return LEGACY_STATUS_TO_NEW[s] || "delivered_to_lab";
}

function statusLabel(status) {
  const normalized = normalizeServiceOrderStatus(status);
  return STATUS_LABELS[normalized] || normalized || "—";
}

export default function TechOperationsModal({
  po,
  open,
  onClose,
  onSaved,
  currentUserId,
}) {
  const [saving, setSaving] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [opsStatus, setOpsStatus] = useState("calibration_finished");
  const [currentSoStatus, setCurrentSoStatus] = useState("delivered_to_lab");
  const [techNotes, setTechNotes] = useState("");
  const [activeSoId, setActiveSoId] = useState(null);

  const explicitServiceOrderId = po?.service_order_id || po?.serviceOrderId || null;

  const isAdminLocked = useMemo(
    () => ADMIN_LOCKED_STATUSES.has(normalizeServiceOrderStatus(currentSoStatus)),
    [currentSoStatus]
  );

  useEffect(() => {
    const loadStatusFromServiceOrders = async () => {
      if (!open || !po) return;

      setLoadingStatus(true);
      setActiveSoId(null);
      setTechNotes(po.ops_tech_notes || "");

      try {
        let row = null;

        if (explicitServiceOrderId) {
          const { data, error } = await supabase
            .from("service_orders")
            .select("id, status")
            .eq("id", explicitServiceOrderId)
            .maybeSingle();
          if (error) throw error;
          row = data || null;
        } else if (po.id) {
          const { data, error } = await supabase
            .from("service_orders")
            .select("id, status")
            .eq("po_id", po.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (error) throw error;
          row = data || null;
        }

        const normalizedCurrent = normalizeServiceOrderStatus(row?.status);
        setCurrentSoStatus(normalizedCurrent);
        setActiveSoId(row?.id || explicitServiceOrderId || null);

        if (TECH_STATUS_OPTIONS.some((opt) => opt.value === normalizedCurrent)) {
          setOpsStatus(normalizedCurrent);
        } else {
          setOpsStatus(TECH_STATUS_OPTIONS[0].value);
        }
      } catch (e) {
        console.error("[TechOperationsModal] load status error:", e);
        const fallback = normalizeServiceOrderStatus(po?.ops_status);
        setCurrentSoStatus(fallback);
        setOpsStatus(
          TECH_STATUS_OPTIONS.some((opt) => opt.value === fallback)
            ? fallback
            : TECH_STATUS_OPTIONS[0].value
        );
      } finally {
        setLoadingStatus(false);
      }
    };

    loadStatusFromServiceOrders();
  }, [open, po, explicitServiceOrderId]);

  if (!open || !po) return null;

  const handleSave = async () => {
    try {
      setSaving(true);

      const statusToSave = isAdminLocked
        ? normalizeServiceOrderStatus(currentSoStatus)
        : normalizeServiceOrderStatus(opsStatus);

      let soQuery = supabase.from("service_orders").update({ status: statusToSave });
      // En dashboard técnico trabajamos por OC: mantener consistencia de todas las OS de la OC.
      if (po?.id) {
        soQuery = soQuery.eq("po_id", po.id);
      } else if (explicitServiceOrderId || activeSoId) {
        soQuery = soQuery.eq("id", explicitServiceOrderId || activeSoId);
      } else {
        throw new Error("No se pudo determinar PO/OS para actualizar estado.");
      }

      const { error: soError } = await soQuery;
      if (soError) throw soError;

      if (po?.id) {
        const { error: poErr } = await supabase
          .from("purchase_orders")
          .update({
            ops_tech_notes: techNotes,
            ops_tech_updated_at: new Date().toISOString(),
            ops_tech_updated_by: currentUserId || null,
          })
          .eq("id", po.id);
        if (poErr) throw poErr;
      }

      const payload = {
        id: po.id,
        service_order_id: explicitServiceOrderId || activeSoId || null,
        status: statusToSave,
        ops_tech_notes: techNotes,
        ops_tech_updated_at: new Date().toISOString(),
        ops_tech_updated_by: currentUserId || null,
      };

      setCurrentSoStatus(statusToSave);
      onSaved && onSaved(payload);
      onClose && onClose();
    } catch (e) {
      console.error("[TechOperationsModal] save error:", e);
      if (e?.code === "42501") {
        alert(
          "Sin permisos para actualizar service_orders.status (RLS). Debes habilitar UPDATE para el técnico en sus OS asignadas."
        );
      } else {
        alert(e.message || "No se pudo guardar el estado de la OS");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-700 bg-slate-900 p-4">
        <h3 className="text-lg font-semibold mb-1">
          Actualización del técnico — {po.po_code || po.code || "OC"}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          <div className="rounded-md border border-slate-700 bg-slate-950/40 p-2">
            <div className="text-[11px] text-slate-400">Prioridad (solo lectura)</div>
            <div className="text-sm text-slate-200">{po.ops_priority || "—"}</div>
          </div>
          <div className="rounded-md border border-slate-700 bg-slate-950/40 p-2">
            <div className="text-[11px] text-slate-400">Vencimiento (solo lectura)</div>
            <div className="text-sm text-slate-200">{po.ops_due_date || "—"}</div>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-md border border-slate-700 bg-slate-950/40 p-2">
            <div className="text-[11px] text-slate-400">Estado actual (service_orders.status)</div>
            <div className="text-sm text-slate-200">
              {loadingStatus ? "Cargando…" : statusLabel(currentSoStatus)}
            </div>
          </div>

          {isAdminLocked ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-100">
              Este estado es administrativo y está bloqueado para edición técnica.
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">
              Estado operativo (editable por técnico)
            </label>
            <select
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 disabled:opacity-60"
              value={opsStatus}
              onChange={(e) => setOpsStatus(e.target.value)}
              disabled={loadingStatus || isAdminLocked}
            >
              {TECH_STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">
              Feedback / observaciones del técnico
            </label>
            <textarea
              rows={4}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 resize-none"
              value={techNotes}
              onChange={(e) => setTechNotes(e.target.value)}
              placeholder="Ej: falta repuesto, esperando confirmación del cliente…"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            className="rounded border border-slate-600/60 px-3 py-1 text-slate-300 hover:bg-slate-600/10"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="rounded border border-emerald-600/60 px-3 py-1 text-emerald-300 hover:bg-emerald-600/10 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving || loadingStatus}
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
